/**
 * A titled panel on finixt-ui's Card: an eyebrow and an icon on a hairline
 * header, the body below, flush by default so a table can sit in it.
 *
 * The header is deliberately quiet, an eyebrow rather than a heading, so a
 * page of four panels reads as content with labels, not four competing
 * titles. Pass flush content (tables, lists) as children; wrap prose in
 * `SectionCardBody` for padding.
 */
import type { CSSProperties, ReactNode } from 'react';

import { Badge, Card, Icon, Typography, type BadgeTone } from './finixt.js';

export interface SectionCardProps {
  /** Omit for a card with no header. */
  title?: string | undefined;
  icon?: string | undefined;
  subtitle?: string | undefined;
  badge?: { label: string; tone?: BadgeTone | undefined } | undefined;
  actions?: ReactNode;
  children: ReactNode;
}

export function SectionCard({ title, icon, subtitle, badge, actions, children }: SectionCardProps) {
  return (
    <Card padding={0}>
      {title ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 11,
            padding: '11px 16px',
            borderBottom: '1px solid var(--border-hairline)',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              {icon ? <Icon name={icon} size={15} color="var(--text-faint)" /> : null}
              <Typography variant="eyebrow">{title}</Typography>
              {badge ? <Badge tone={badge.tone ?? 'neutral'}>{badge.label}</Badge> : null}
            </div>
            {subtitle ? (
              <Typography variant="meta" as="p" style={{ margin: '7px 0 0', maxWidth: '65ch' }}>
                {subtitle}
              </Typography>
            ) : null}
          </div>
          {actions ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
              {actions}
            </div>
          ) : null}
        </div>
      ) : null}
      {children}
    </Card>
  );
}

/** Padded prose inside a SectionCard. */
export function SectionCardBody({
  children,
  style,
}: {
  children: ReactNode;
  style?: CSSProperties | undefined;
}) {
  return <div style={{ padding: 16, ...style }}>{children}</div>;
}
